// ---------------------------------------------------------------------------
// Pantalla de la integración con Mercado Libre.
//
// Todo lo que escribe pasa por el servidor: ml_accounts y ml_listings no
// aceptan escrituras desde el navegador. Conectar/desconectar va por la edge
// function ml-oauth; configuración, vínculos y acciones por RPCs que validan
// rol de administrador y pertenencia (ml_update_config, ml_link_listing, …).
//
// Espejo del patrón de IntegracionTiendaNube.jsx. El nombre no empieza con
// "Tienda": ese glob está PROTECTED en el sync y no viajaría a Gastify.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import { ML_ORDER_STATUS_LABELS, ML_MOTIVO_LABELS } from '../utils/mercadoLibre';

const ERRORES = {
  no_configurado: 'Mercado Libre no está configurado en este entorno. Avisanos.',
  state_invalido: 'El pedido de conexión venció o no es válido. Volvé a intentar desde acá.',
  autorizacion_rechazada: 'No se dio permiso en Mercado Libre.',
  sin_code: 'Mercado Libre no devolvió el código de autorización. Probá de nuevo.',
  canje_fallido: 'No se pudo canjear el código con Mercado Libre. Probá de nuevo.',
  token_no_guardado: 'Mercado Libre autorizó, pero no se pudo guardar el acceso de forma segura.',
  no_se_pudo_guardar: 'No se pudo guardar la conexión. Si sigue pasando, avisanos.',
  cuenta_de_otra_org: 'Esa cuenta de Mercado Libre ya está conectada a otra empresa. Desconectala allá primero.',
  inesperado: 'Algo falló al conectar. Probá de nuevo.',
};

const BTN = 'px-4 py-2 rounded-xl bg-slate-900 text-white text-xs';
const BTN_SEC = 'px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs';
const CANAL_OAUTH = 'ml-oauth';
const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'sin_vincular', label: 'Sin vincular' },
  { id: 'problemas', label: 'Con problemas' },
];

async function invocar(action, extra = {}) {
  const { data, error } = await supabase.functions.invoke('ml-oauth', { body: { action, ...extra } });
  if (error) {
    let detalle = null;
    try { detalle = await error.context?.json?.(); } catch { /* sin cuerpo */ }
    throw new Error(detalle?.error || error.message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

function avisarConectada() {
  if (typeof BroadcastChannel === 'undefined') return;
  const canal = new BroadcastChannel(CANAL_OAUTH);
  canal.postMessage({ type: 'connected' });
  canal.close();
  setTimeout(() => { try { window.close(); } catch { /* queda abierta */ } }, 1500);
}

function SelectorProducto({ orgId, onElegir, disabled }) {
  const [texto, setTexto] = useState('');
  const [opciones, setOpciones] = useState([]);

  useEffect(() => {
    const q = texto.trim();
    if (q.length < 2) return undefined;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('products')
        .select('id, name, barcode')
        .eq('organization_id', orgId)
        .is('archived_at', null)
        .ilike('name', `%${q.replace(/[%_]/g, '')}%`)
        .order('name')
        .limit(15);
      setOpciones(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [texto, orgId]);

  return (
    <div className="relative">
      <input
        className="w-full border border-slate-200 rounded-lg px-2 py-1 text-xs"
        placeholder="Buscar producto del ERP…"
        value={texto}
        disabled={disabled}
        onChange={(e) => {
          setTexto(e.target.value);
          if (e.target.value.trim().length < 2) setOpciones([]);
        }}
      />
      {opciones.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-sm max-h-56 overflow-auto">
          {opciones.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50"
                onClick={() => {
                  setTexto('');
                  setOpciones([]);
                  onElegir(p);
                }}
              >
                {p.name}
                {p.barcode ? <span className="text-slate-400"> · {p.barcode}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IntegracionMercadoLibre() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const esAdmin = ['admin', 'administrador'].includes(String(profile?.role || '').trim().toLowerCase());

  const [searchParams, setSearchParams] = useSearchParams();
  const [cuenta, setCuenta] = useState(null);
  const [listings, setListings] = useState([]);
  const [productos, setProductos] = useState({});
  const [ordenes, setOrdenes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [medios, setMedios] = useState([]);
  const [listas, setListas] = useState([]);
  const [form, setForm] = useState({
    site_id: '', payment_method_id: '', price_list_id: '', sync_stock: false, sync_price: false, default_stock_buffer: 0,
  });
  const [filtro, setFiltro] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [ocupado, setOcupado] = useState('');
  const claimsProcesados = useRef(new Set());

  const fetchEstado = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    const { data: acc, error } = await supabase.from('ml_accounts_safe').select('*').maybeSingle();
    if (error) console.error(error);
    setCuenta(acc || null);

    if (acc) {
      // PostgREST corta en 1000 filas por pedido: se pagina.
      const traerPublicaciones = async () => {
        const todas = [];
        for (let desde = 0; desde < 20000; desde += 1000) {
          const { data, error: lisError } = await supabase.from('ml_listings')
            .select('id, ml_item_id, ml_variation_id, title, sku, ml_status, ml_price, ml_available_quantity, permalink, product_id, variant_id, link_source, sync_stock, sync_price, stock_buffer, sync_state, sync_reason, last_error, last_synced_stock, last_synced_price, last_synced_at')
            .eq('ml_account_id', acc.id)
            .order('title')
            .order('id')
            .range(desde, desde + 999);
          if (lisError) {
            console.error(lisError);
            break;
          }
          todas.push(...(data || []));
          if (!data || data.length < 1000) break;
        }
        return { data: todas };
      };
      const [lisRes, ordRes, logRes, sitRes, pmRes, plRes] = await Promise.all([
        traerPublicaciones(),
        supabase.from('ml_orders')
          .select('id, ml_order_id, status, error_detail, total_amount, buyer_nickname, updated_at, sale_id')
          .eq('ml_account_id', acc.id)
          .in('status', ['pendiente', 'sin_vincular', 'error', 'conflicto'])
          .order('updated_at', { ascending: false })
          .limit(50),
        supabase.from('ml_sync_log')
          .select('id, level, event, message, created_at')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).eq('is_active', true).order('nombre'),
        supabase.from('payment_methods').select('id, name')
          .eq('organization_id', orgId).eq('is_active', true).is('archived_at', null).order('name'),
        supabase.from('price_lists').select('id, name').eq('organization_id', orgId).eq('is_active', true).order('name'),
      ]);
      const lis = lisRes.data || [];
      setListings(lis);
      setOrdenes(ordRes.data || []);
      setLogs(logRes.data || []);
      setSitios(sitRes.data || []);
      setMedios(pmRes.data || []);
      setListas(plRes.data || []);

      const ids = [...new Set(lis.map((l) => l.product_id).filter(Boolean))];
      if (ids.length) {
        const mapa = {};
        for (let i = 0; i < ids.length; i += 200) {
          const { data: prods } = await supabase.from('products').select('id, name').in('id', ids.slice(i, i + 200));
          (prods || []).forEach((p) => { mapa[p.id] = p.name; });
        }
        setProductos(mapa);
      } else {
        setProductos({});
      }

      setForm({
        site_id: acc.fulfillment_site_id || '',
        payment_method_id: acc.payment_method_id || '',
        price_list_id: acc.price_list_id || '',
        sync_stock: acc.sync_stock === true,
        sync_price: acc.sync_price === true,
        default_stock_buffer: Number(acc.default_stock_buffer || 0),
      });
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchEstado(); }, [fetchEstado]);

  useEffect(() => {
    const alVolver = () => { if (document.visibilityState === 'visible') fetchEstado(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    let canal = null;
    if (typeof BroadcastChannel !== 'undefined') {
      canal = new BroadcastChannel(CANAL_OAUTH);
      canal.onmessage = (ev) => {
        if (ev?.data?.type === 'connected') {
          toast.success('Mercado Libre conectado');
          fetchEstado();
        }
      };
    }
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      if (canal) canal.close();
    };
  }, [fetchEstado]);

  // Vuelta del OAuth: ?ml_claim=<id>&k=<secreto> o ?error=<código>. El secreto
  // solo llega a la pestaña que volvió de autorizar en Mercado Libre.
  useEffect(() => {
    const error = searchParams.get('error');
    const claimId = searchParams.get('ml_claim');
    const claimSecret = searchParams.get('k') || '';
    if (!error && !claimId) return;
    setSearchParams({}, { replace: true });
    if (error) toast.error(ERRORES[error] || ERRORES.inesperado);
    if (claimId && !claimsProcesados.current.has(claimId)) {
      claimsProcesados.current.add(claimId);
      invocar('claim', { claim_id: claimId, k: claimSecret })
        .then((res) => {
          toast.success(`Mercado Libre conectado${res?.nickname ? `: ${res.nickname}` : ''}. Importando publicaciones…`);
          avisarConectada();
          fetchEstado();
        })
        .catch((e) => toast.error(ERRORES[e.message] || e.message));
    }
  }, [searchParams, setSearchParams, fetchEstado]);

  const handleConectar = async () => {
    setOcupado('conectar');
    let ventana = null;
    try {
      ventana = window.open('about:blank', '_blank');
      if (ventana) ventana.opener = null;
    } catch {
      ventana = null;
    }
    try {
      const data = await invocar('start');
      if (!data?.url) throw new Error('Sin URL de autorización');
      if (ventana && !ventana.closed) {
        ventana.location.href = data.url;
        toast('Autorizá la aplicación en la pestaña que se abrió', { icon: '🔗' });
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      if (ventana && !ventana.closed) ventana.close();
      toast.error(e.message || 'No se pudo iniciar la conexión con Mercado Libre');
    } finally {
      setOcupado('');
    }
  };

  const handleDesconectar = async () => {
    if (!window.confirm(
      'Se dejan de recibir ventas y de enviar stock y precios. Las ventas registradas y los vínculos '
      + 'de publicaciones se conservan. ¿Desconectar?',
    )) return;
    setOcupado('desconectar');
    try {
      await invocar('disconnect');
      toast.success('Mercado Libre desconectado');
      await fetchEstado();
    } catch (e) {
      toast.error(e.message || 'No se pudo desconectar');
    } finally {
      setOcupado('');
    }
  };

  const rpc = async (clave, nombre, params, exito) => {
    setOcupado(clave);
    const { data, error } = await supabase.rpc(nombre, params);
    setOcupado('');
    if (error) {
      toast.error(error.message);
      return null;
    }
    if (exito) toast.success(typeof exito === 'function' ? exito(data) : exito);
    await fetchEstado();
    return data ?? true;
  };

  const handleGuardar = () => rpc('guardar', 'ml_update_config', {
    p_site_id: form.site_id || null,
    p_payment_method_id: form.payment_method_id || null,
    p_price_list_id: form.price_list_id || null,
    p_sync_stock: form.sync_stock,
    p_sync_price: form.sync_price,
    p_default_stock_buffer: Math.max(0, Math.floor(Number(form.default_stock_buffer) || 0)),
  }, 'Configuración guardada');

  const handleAccion = (accion, mensaje) => rpc(accion, 'ml_request_action', { p_action: accion }, mensaje);

  const handleVincular = async (listing, producto) => {
    const res = await rpc(`l-${listing.id}`, 'ml_link_listing', { p_listing: listing.id, p_product: producto.id },
      `Vinculada a ${producto.name}`);
    if (res?.variantes > 1) {
      toast('Ese producto tiene variantes: el ERP lleva un solo stock por producto, así que su stock no se va a publicar.', { icon: '⚠️' });
    }
  };

  const listingsFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return listings.filter((l) => {
      if (filtro === 'sin_vincular' && l.product_id) return false;
      if (filtro === 'problemas' && !['bloqueado', 'error'].includes(l.sync_state)) return false;
      if (!q) return true;
      return [l.title, l.sku, l.ml_item_id, productos[l.product_id]]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [listings, filtro, busqueda, productos]);

  if (loading) return <div className="p-6 text-slate-500">Cargando…</div>;
  if (!orgId) return <div className="p-6 text-slate-500">No se encontró la empresa de tu usuario.</div>;

  const conectada = cuenta?.is_active === true;
  const operativa = conectada && !cuenta?.needs_reauth;
  const multiSitio = sitios.length > 1;
  const vinculadas = listings.filter((l) => l.product_id).length;
  const hayParaReintentar = ordenes.some((o) => ['pendiente', 'sin_vincular', 'error'].includes(o.status));
  const estado = !cuenta
    ? null
    : !conectada
      ? { label: 'Desconectada', clase: 'bg-slate-100 text-slate-600' }
      : cuenta.needs_reauth
        ? { label: 'Requiere reconectar', clase: 'bg-red-50 text-red-700' }
        : { label: 'Conectada', clase: 'bg-emerald-50 text-emerald-700' };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Mercado Libre</h1>
        <p className="text-slate-500 mt-1">
          Las ventas cobradas en Mercado Libre entran como ventas del ERP (stock, caja y libro diario), listas para
          facturar. Si lo activás, el stock y los precios del ERP se publican en tus publicaciones vinculadas.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        {!cuenta && (
          <div className="text-center py-6">
            <p className="text-slate-600 mb-4">Todavía no conectaste ninguna cuenta.</p>
            <div className="flex justify-center">
              <Button className={BTN} onClick={handleConectar} disabled={!esAdmin} isLoading={ocupado === 'conectar'}>
                Conectar con Mercado Libre
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              {esAdmin
                ? 'Te vamos a pedir permiso en Mercado Libre. Solo entran las ventas posteriores a la conexión.'
                : 'Solo un administrador puede conectar la cuenta.'}
            </p>
          </div>
        )}

        {cuenta && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">{cuenta.nickname || `Usuario ${cuenta.ml_user_id}`}</div>
                <div className="text-sm text-slate-500">
                  Conectada desde {cuenta.connected_at ? new Date(cuenta.connected_at).toLocaleString('es-AR') : '—'}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${estado.clase}`}>{estado.label}</span>
            </div>

            {cuenta.last_error && conectada && (
              <div className="bg-red-50 text-red-700 text-sm rounded-xl p-3">{cuenta.last_error}</div>
            )}

            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-slate-400">Publicaciones</dt>
                <dd className="text-slate-700">{listings.length}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Vinculadas</dt>
                <dd className="text-slate-700">{vinculadas}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Última importación</dt>
                <dd className="text-slate-700">
                  {cuenta.last_catalog_sync_at
                    ? new Date(cuenta.last_catalog_sync_at).toLocaleString('es-AR')
                    : 'En curso o pendiente'}
                </dd>
              </div>
            </dl>

            {esAdmin && (
              <div className="flex gap-2 flex-wrap pt-2">
                {operativa ? (
                  <>
                    <Button className={BTN} onClick={() => handleAccion('catalog', 'Importación en curso: en un par de minutos aparece en Actividad')} isLoading={ocupado === 'catalog'}>
                      Importar publicaciones
                    </Button>
                    {(cuenta.sync_stock || cuenta.sync_price) && (
                      <Button className={BTN_SEC} onClick={() => handleAccion('push_all', (n) => `${n} producto(s) en cola`)} isLoading={ocupado === 'push_all'}>
                        Reenviar stock y precios
                      </Button>
                    )}
                    <Button className={BTN_SEC} onClick={handleDesconectar} isLoading={ocupado === 'desconectar'}>
                      Desconectar
                    </Button>
                  </>
                ) : (
                  <Button className={BTN} onClick={handleConectar} isLoading={ocupado === 'conectar'}>
                    Volver a conectar
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {cuenta && conectada && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <h2 className="font-semibold text-slate-800">Configuración</h2>
          {multiSitio && !form.site_id && (
            <div className="bg-amber-50 text-amber-800 text-sm rounded-xl p-3">
              Tenés más de una sucursal: elegí de cuál sale el stock de Mercado Libre. Hasta entonces las ventas quedan en espera.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <label className="space-y-1 block">
              <span className="text-slate-500">Sucursal que descuenta stock</span>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white" value={form.site_id} disabled={!esAdmin}
                onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}>
                <option value="">{multiSitio ? 'Elegí una sucursal' : 'La única sucursal activa'}</option>
                {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </label>
            <label className="space-y-1 block">
              <span className="text-slate-500">Medio de cobro de las ventas</span>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white" value={form.payment_method_id} disabled={!esAdmin}
                onChange={(e) => setForm((f) => ({ ...f, payment_method_id: e.target.value }))}>
                <option value="">Sin medio (no impacta en bancos)</option>
                {medios.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <span className="block text-xs text-slate-400">
                Usá un medio con la comisión de Mercado Libre y sus días de acreditación: el asiento va por el neto.
              </span>
            </label>
            <label className="space-y-1 block">
              <span className="text-slate-500">Lista de precios del canal</span>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white" value={form.price_list_id} disabled={!esAdmin}
                onChange={(e) => setForm((f) => ({ ...f, price_list_id: e.target.value }))}>
                <option value="">Ninguna (no se publican precios)</option>
                {listas.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label className="space-y-1 block">
              <span className="text-slate-500">Colchón de stock para publicaciones nuevas</span>
              <input type="number" min="0" className="w-full border border-slate-200 rounded-lg px-3 py-2" value={form.default_stock_buffer} disabled={!esAdmin}
                onChange={(e) => setForm((f) => ({ ...f, default_stock_buffer: e.target.value }))} />
            </label>
            <label className="flex items-start gap-2 sm:col-span-2">
              <input type="checkbox" className="mt-1" checked={form.sync_stock} disabled={!esAdmin}
                onChange={(e) => setForm((f) => ({ ...f, sync_stock: e.target.checked }))} />
              <span>
                <span className="text-slate-700">Publicar el stock del ERP en Mercado Libre</span>
                <span className="block text-xs text-slate-400">
                  Se publica el stock de la sucursal elegida menos el colchón de cada publicación. El ERP es la fuente de
                  verdad: lo que cambies a mano en Mercado Libre se pisa. Los productos con varios talles o colores no se
                  publican: el ERP lleva un solo stock por producto.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 sm:col-span-2">
              <input type="checkbox" className="mt-1" checked={form.sync_price} disabled={!esAdmin || !form.price_list_id}
                onChange={(e) => setForm((f) => ({ ...f, sync_price: e.target.checked }))} />
              <span>
                <span className="text-slate-700">Publicar los precios de la lista del canal</span>
                <span className="block text-xs text-slate-400">
                  El precio de mostrador nunca viaja a Mercado Libre. Elegí primero la lista de precios del canal.
                </span>
              </span>
            </label>
          </div>
          {esAdmin ? (
            <Button className={BTN} onClick={handleGuardar} isLoading={ocupado === 'guardar'}>Guardar</Button>
          ) : (
            <p className="text-xs text-slate-400">Solo un administrador puede cambiar la configuración.</p>
          )}
        </div>
      )}

      {cuenta && ordenes.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <h2 className="font-semibold text-slate-800">Ventas que todavía no entraron</h2>
            {esAdmin && operativa && hayParaReintentar && (
              <Button className={BTN_SEC} onClick={() => handleAccion('retry_orders', (n) => (n ? `${n} orden(es) en cola` : 'No había órdenes para reintentar'))} isLoading={ocupado === 'retry_orders'}>
                Reintentar
              </Button>
            )}
          </div>
          <ul className="divide-y divide-slate-100">
            {ordenes.map((o) => (
              <li key={o.id} className="py-2 text-sm flex items-start gap-3">
                <span className="font-mono text-slate-500 flex-shrink-0">#{o.ml_order_id}</span>
                <span className="flex-1 text-slate-700 min-w-0 break-words">
                  {o.error_detail || '—'}
                  {o.buyer_nickname ? <span className="text-slate-400"> · {o.buyer_nickname}</span> : null}
                </span>
                <span className="text-xs text-slate-400 flex-shrink-0">{ML_ORDER_STATUS_LABELS[o.status] || o.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cuenta && listings.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-slate-800">Publicaciones</h2>
            <div className="flex gap-2 flex-wrap items-center">
              {FILTROS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFiltro(f.id)}
                  className={`px-3 py-1 rounded-full text-xs border ${filtro === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                  {f.label}
                </button>
              ))}
              <input className="border border-slate-200 rounded-lg px-3 py-1 text-xs" placeholder="Buscar…"
                value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Sin vínculo, una publicación no se toca y sus ventas quedan en espera. Las que tienen el mismo SKU que un
            producto del ERP se vinculan solas al importar.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Publicación</th>
                  <th className="py-2 pr-3">Producto del ERP</th>
                  <th className="py-2 pr-3 text-center">Stock</th>
                  <th className="py-2 pr-3 text-center">Precio</th>
                  <th className="py-2 pr-3 text-center">Colchón</th>
                  <th className="py-2">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {listingsFiltradas.slice(0, 300).map((l) => {
                  const ocupada = ocupado === `l-${l.id}`;
                  return (
                    <tr key={l.id} className="align-top">
                      <td className="py-2 pr-3 min-w-[14rem]">
                        <div className="text-slate-700">{l.title || l.ml_item_id}</div>
                        <div className="text-slate-400">
                          {l.permalink ? <a href={l.permalink} target="_blank" rel="noreferrer" className="hover:underline">{l.ml_item_id}</a> : l.ml_item_id}
                          {l.sku ? ` · SKU ${l.sku}` : ''}
                          {l.ml_status && l.ml_status !== 'active' ? ` · ${l.ml_status}` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-3 min-w-[12rem]">
                        {l.product_id ? (
                          <div className="flex items-start gap-2">
                            <span className="text-slate-700">{productos[l.product_id] || 'Producto'}</span>
                            {esAdmin && (
                              <button type="button" className="text-slate-400 hover:text-red-600" disabled={ocupada}
                                onClick={() => rpc(`l-${l.id}`, 'ml_unlink_listing', { p_listing: l.id }, 'Desvinculada')}>
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <SelectorProducto orgId={orgId} disabled={!esAdmin || ocupada} onElegir={(p) => handleVincular(l, p)} />
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <input type="checkbox" checked={l.sync_stock} disabled={!esAdmin || ocupada}
                          onChange={(e) => rpc(`l-${l.id}`, 'ml_update_listing', { p_listing: l.id, p_sync_stock: e.target.checked, p_sync_price: null, p_stock_buffer: null })} />
                        {l.last_synced_stock != null && <div className="text-slate-400">{l.last_synced_stock}</div>}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <input type="checkbox" checked={l.sync_price} disabled={!esAdmin || ocupada}
                          onChange={(e) => rpc(`l-${l.id}`, 'ml_update_listing', { p_listing: l.id, p_sync_stock: null, p_sync_price: e.target.checked, p_stock_buffer: null })} />
                        {l.last_synced_price != null && <div className="text-slate-400">${Number(l.last_synced_price).toLocaleString('es-AR')}</div>}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <input type="number" min="0" className="w-14 border border-slate-200 rounded px-1 py-0.5 text-center"
                          defaultValue={l.stock_buffer} disabled={!esAdmin || ocupada}
                          onBlur={(e) => {
                            const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                            if (v !== Number(l.stock_buffer)) {
                              rpc(`l-${l.id}`, 'ml_update_listing', { p_listing: l.id, p_sync_stock: null, p_sync_price: null, p_stock_buffer: v });
                            }
                          }} />
                      </td>
                      <td className="py-2 text-slate-500 min-w-[10rem]">
                        {!l.product_id && <span className="text-amber-700">Sin vincular</span>}
                        {l.product_id && l.sync_state === 'ok' && <span className="text-emerald-700">Sincronizada</span>}
                        {l.product_id && l.sync_state === 'bloqueado' && (
                          <span className="text-amber-700">{ML_MOTIVO_LABELS[l.sync_reason] || l.sync_reason}</span>
                        )}
                        {l.product_id && l.sync_state === 'error' && <span className="text-red-700">{l.last_error || 'Error'}</span>}
                        {l.product_id && !l.sync_state && <span>Pendiente</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {listingsFiltradas.length > 300 && (
              <p className="text-xs text-slate-400 mt-2">Mostrando 300 de {listingsFiltradas.length}. Usá la búsqueda para encontrar el resto.</p>
            )}
          </div>
        </div>
      )}

      {cuenta && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-slate-800 mb-3">Actividad</h2>
          {logs.length === 0 && <p className="text-sm text-slate-400">Sin movimientos todavía.</p>}
          <ul className="divide-y divide-slate-100">
            {logs.map((log) => (
              <li key={log.id} className="py-2 flex items-start gap-3 text-sm">
                <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${log.level === 'error' ? 'bg-red-500' : log.level === 'warn' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 break-words">{log.message}</div>
                  <div className="text-xs text-slate-400">{log.event} · {new Date(log.created_at).toLocaleString('es-AR')}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default IntegracionMercadoLibre;
